import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AlertMessage from '~/components/data-display/AlertMessage';
import Radio from '~/components/inputs/Radio/Radio';
import RadioGroup from '~/components/inputs/RadioGroup/RadioGroup';
import { VStack } from '~/components/layout/Stack';
import Modal from '~/components/modals/Modal/Modal';
import ModalContent from '~/components/modals/Modal/ModalContent';
import ModalTitle from '~/components/modals/Modal/ModalTitle';
import SimpleModalActions from '~/components/modals/Modal/SimpleModalActions';
import Text from '~/components/typographies/Text';
import { ModalProps } from '~/modals/Modal';
import styles from './BookkeepingTransactionInternalTransferDialog.module.scss';

enum InternalTransferChoice {
  KEEP = 'Keep',
  UNLINK = 'Unlink',
}

type InternalTransferDialogAction = 'CLOSE' | 'KEEP' | 'UNLINK';
interface BookkeepingTransactionInternalTransferDialogProps extends ModalProps {
  closeModal: (props?: { action: InternalTransferDialogAction }) => void;
}

const BookkeepingTransactionInternalTransferDialog = ({
  closeModal,
}: BookkeepingTransactionInternalTransferDialogProps) => {
  const { t } = useTranslation('bookkeeping', {
    keyPrefix: 'transactions.update.internal_transfer_dialog',
  });
  const [choice, setChoice] = useState<InternalTransferChoice | undefined>(undefined);
  const [hasError, setHasError] = useState(false);

  return (
    <Modal
      isOpen
      onOpenChange={(open) => {
        if (!open) closeModal();
      }}
      dialogClassName={styles.dialog}
      maxWidth={600}
      fullWidth
    >
      <ModalTitle onCloseClick={() => closeModal()}>{t('title')}</ModalTitle>
      <ModalContent>
        <VStack gap={3}>
          <Text variant="body1" weight="regular">
            {t('body')}
          </Text>
          <VStack gap={2} className={styles.radioGroupContainer}>
            <RadioGroup
              ariaLabel={t('body')}
              value={choice}
              onChange={(value) => {
                setChoice(value as InternalTransferChoice);
                setHasError(false);
              }}
            >
              <VStack gap={3}>
                <Radio value={InternalTransferChoice.KEEP} strong otherInfo={t('keep_link_description')}>
                  {t('keep_link')}
                </Radio>
                <Radio value={InternalTransferChoice.UNLINK} strong otherInfo={t('unlink_description')}>
                  {t('unlink')}
                </Radio>
              </VStack>
            </RadioGroup>

            {hasError && (
              <AlertMessage box type="error">
                {t('error_no_selection')}
              </AlertMessage>
            )}
          </VStack>
        </VStack>
      </ModalContent>
      <SimpleModalActions
        confirmLabel={t('save')}
        cancelLabel={t('cancel')}
        onConfirm={() => {
          if (!choice) {
            setHasError(true);
            return;
          }
          closeModal({ action: choice === InternalTransferChoice.KEEP ? 'KEEP' : 'UNLINK' });
        }}
        onOpenChange={(open) => {
          if (!open) closeModal();
        }}
      />
    </Modal>
  );
};

export default BookkeepingTransactionInternalTransferDialog;
